import Head from 'next/head';
import { useEffect } from 'react';
import PropTypes from 'prop-types';
import SEO from '@seo/index';
import { SWRConfig } from 'swr';
import { useSetSelectedNav } from '@stores/sidenav';
import { PlusTopologySource } from '@constants/index';
import { fetchTopologyFeed } from '@actions/topologies';
import plusNavBarItems from '@layout/NavBar/PlusNavBar/plusNavBarItems';
import subGoalSchema from '@seo/goal/subGoal';
import PlusTopology from '@cont/PlusTopics/PlusTopology';
import { SYLLABUS } from '@constants/segment';
import { useSelector, useDispatch } from 'react-redux';
import { getIsTopologyFeedLoaded } from '@selectors/syllabus';
import SyllabusHomePage from '@cont/Syllabus/HomePage/HomePage';
import { useRouter } from 'next/router';
import GTPMobileBlocker from '@cont/GTP/GTPMobileBlocker';
import useWindowSize from '@hooks/useWindowSize';
import Layout from '@layout/index';
import PageReduxProvider from '@cont/App/PageReduxProviderWrapper';
import { usePageSeoContent } from '@stores/AppCommonData';
import fetchPageMetaInfo from '@api/fetchers/pageMetaInfo';
import { fetchConstants } from '@api/fetchers/constants';
import { fetchSyllabusData } from '@api/fetchers/goal/topics';
import { fetchGoalInfo } from '@api/fetchers/goal';
import { isGTPGoal } from '@api/selectors/constant';
import resolvePromiseToObject from '@utils/resolvePromiseToObj';
import useFullSyllabusInfo from '@api/hooks/goal/topics/useFullSyllabusInfo';
import { getFullName } from '@models/User';

const PlusTopicsPage = ({
  goalInfo,
  fallbackData,
  isNewSyllabusEnabled,
  isGTP,
  isRecordedGoal
}) => {
  const { query } = useRouter();
  const {
    name = 'Goal',
    thumbnail,
    ogImage,
    slug: goalSlug,
    uid: goalUID
  } = goalInfo ?? {};

  const { keys } = useFullSyllabusInfo(
    goalUID,
    fallbackData,
    isNewSyllabusEnabled,
    isRecordedGoal
  );
  const { title, description } = usePageSeoContent();
  const dispatch = useDispatch();
  const loadedTopologyFeed = useSelector(getIsTopologyFeedLoaded);
  const { isMobile } = useWindowSize();
  const metaTitle = title || `Important topics for ${name} - Unacademy`;
  const metaDescription =
    description || `Explore various courses topic wise to prepare for ${name}`;
  const canonicalURL = `https://unacademy.com/goal/${goalSlug}/${goalUID}/topics`;
  useEffect(() => {
    if (!isNewSyllabusEnabled && !loadedTopologyFeed) {
      dispatch(
        fetchTopologyFeed({
          topologyUid: goalUID,
          pageSource: PlusTopologySource.TOPICS
        })
      );
    }
  }, [isNewSyllabusEnabled, isGTP, goalUID, dispatch, loadedTopologyFeed]);

  useSetSelectedNav(plusNavBarItems.SYLLABUS);

  if (isGTP && isMobile) {
    return <GTPMobileBlocker goalInfo={goalInfo || {}} />;
  }
  const renderSyllabusHomePage = () => {
    if (isNewSyllabusEnabled)
      return (
        <SyllabusHomePage
          source={PlusTopologySource.TOPICS}
          goalUID={goalUID}
          topicUID={query.topicUid}
          isGTP={isGTP}
          isRecordedGoal={isRecordedGoal}
          topicFilters={fallbackData.topicFilters}
        />
      );

    return (
      <PlusTopology
        source={PlusTopologySource.TOPICS}
        goalUID={goalUID}
        topologyUID={goalUID}
      />
    );
  };

  return (
    <SWRConfig
      value={{
        fallback: {
          [keys.syllabusSWRKey]: fallbackData.syllabusData,
          [keys.goalInfoSWRKey]: fallbackData.goalData
        }
      }}
    >
      <SEO
        title={metaTitle}
        description={metaDescription}
        openGraph={{
          metaTitle,
          metaDescription,
          images: [
            {
              url: ogImage || thumbnail
            }
          ],
          url: canonicalURL
        }}
        schema={subGoalSchema({
          goal: goalInfo,
          description: metaDescription,
          subGoal: { name: 'Syllabus', link: 'topics' }
        })}
      />
      <Head>
        <title>{metaTitle}</title>
      </Head>
      {renderSyllabusHomePage()}
    </SWRConfig>
  );
};

PlusTopicsPage.pageInfo = {
  isMWebSupported: true,
  name: 'Syllabus',
  layoutOptions: {
    showPlusNav: true
  }
};

export default PlusTopicsPage;

export const getStaticProps = async ({ params: query }) => {
  try {
    const { goalUID } = query;
    const [constants, goalInfo] = await Promise.all([
      fetchConstants(),
      fetchGoalInfo(goalUID)
    ]);

    const isGTP = isGTPGoal(constants, goalUID);

    const isNewSyllabusEnabled = goalInfo?.isSyllabusV1Enabled;

    const promises = [];
    if (isNewSyllabusEnabled) {
      promises.push(
        resolvePromiseToObject(
          fetchSyllabusData(goalUID, goalInfo?.isRecordedGoal),
          'syllabusData'
        )
      );
    }

    if (isGTP) {
      promises.push(
        resolvePromiseToObject(
          fetchSyllabusData(goalUID, goalInfo?.isRecordedGoal),
          'syllabusData'
        )
      );
    }

    const fallbackData = await Promise.all(promises).then((resolvedValues) =>
      resolvedValues.reduce(
        (acc, next) => ({ ...acc, [next.type]: next.result }),
        {}
      )
    );
    fallbackData.goalData = goalInfo;
    const pageMetaInfo = await fetchPageMetaInfo(SYLLABUS, goalUID);

    if (goalInfo?.isRecordedGoal) {
      const educatorOptions = [
        { label: 'All educators', value: 'all' },
        ...(fallbackData?.syllabusData?.authors?.map((educator) => {
          const { firstName, lastName, uid } = educator;
          return {
            label: getFullName(firstName, lastName),
            value: uid
          };
        }) || [])
      ];

      const subjectOptions = [
        { label: 'All subjects', value: 'all' },
        ...(fallbackData?.syllabusData?.data?.map((topicGroup) => ({
          label: topicGroup.title,
          value: topicGroup.uid
        })) || [])
      ];

      fallbackData.topicFilters = {
        educatorOptions,
        subjectOptions
      };
    }

    return {
      props: {
        isGTP,
        goalInfo,
        isNewSyllabusEnabled,
        fallbackData,
        pageMetaInfo,
        isRecordedGoal: goalInfo?.isRecordedGoal
      },
      revalidate: 60
    };
  } catch (error) {
    return {
      notFound: true,
      revalidate: 60
    };
  }
};

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking'
  };
};

PlusTopicsPage.propTypes = {
  goalInfo: PropTypes.objectOf(PropTypes.unknown).isRequired
};
PlusTopicsPage.Layout = Layout;
PlusTopicsPage.Wrapper = PageReduxProvider;
